import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useNavigationType } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { initBridgeListener, sendWebReady, onRNMessage, sendToRN } from './bridge/webBridge';
import { saveFcmToken } from './services/fcmTokenService';
import { isMessengerOpen, closeChannelTalk, subscribeMessengerState } from './services/channelTalkService';
import { auditRouteView, auditWebViewMessage } from './services/auditLogService';
import Spinner from './components/common/Spinner';
import ChannelTalkModal from './components/common/ChannelTalkModal';
import RequireAuth from './components/guards/RequireAuth';
import RequireRole from './components/guards/RequireRole';
import RequireConsent from './components/guards/RequireConsent';

// 레이아웃
import AdminLayout from './components/layout/AdminLayout';
import PartnerLayout from './components/layout/PartnerLayout';
import RealtorLayout from './components/layout/RealtorLayout';

// 시스템
import LoginPage from './pages/Login/LoginPage';
import PartnerLoginPage from './pages/Login/PartnerLoginPage';
import RealtorAutoConnectPage from './pages/Login/RealtorAutoConnectPage';
import AdminLoginPage from './pages/Login/AdminLoginPage';
import SignupPage from './pages/Login/SignupPage';
import ForgotPasswordPage from './pages/Login/ForgotPasswordPage';
import PaymentSuccess from './pages/Payment/PaymentSuccess';
import PaymentFail from './pages/Payment/PaymentFail';
import TermsPage from './pages/Login/TermsPage';
import PrivacyPage from './pages/Public/PrivacyPage';
import AccountDeletionPage from './pages/Public/AccountDeletionPage';
// 알림톡 short-link 응답 화면 (public)
import RespondInputPage from './pages/Public/RespondInputPage';
import ReviewPage from './pages/Public/ReviewPage';
import ChangePasswordPage from './pages/Login/ChangePasswordPage';
import ConsentPage from './pages/Consent/ConsentPage';
// 소셜 로그인 플로우
import SocialRoleSelectPage from './pages/Login/Social/SocialRoleSelectPage';
import LinkPhonePage from './pages/Login/Social/LinkPhonePage';
import MergeConfirmPage from './pages/Login/Social/MergeConfirmPage';
import NoAccountPage from './pages/Login/Social/NoAccountPage';
import SocialSignupPage from './pages/Login/Social/SocialSignupPage';
import SignupCompletePage from './pages/Login/Social/SignupCompletePage';
import KakaoCallbackPage from './pages/Login/Social/KakaoCallbackPage';

// 고객 신청 폼 (public)
import FormPage from './pages/Customer/FormPage';
import FormSuccessPage from './pages/Customer/FormSuccessPage';
import CustomerMyPage from './pages/Customer/CustomerMyPage';

// 고객 신청 위저드 (public)
import ApplyPage from './pages/Customer/ApplyPage';
import ApplySuccessPage from './pages/Customer/ApplySuccessPage';

// 제휴 신청 (public)
import RealtorSignupPage from './pages/Signup/RealtorSignupPage';
import PartnerSignupPage from './pages/Signup/PartnerSignupPage';

// 관리자 — 대시보드
import AdminDashboard from './pages/Admin/Dashboard/AdminDashboard';
import AdminMailPage from './pages/Admin/Mail/AdminMailPage';
// 관리자 — 회원관리
import PartnerList from './pages/Admin/Members/PartnerList';
import PartnerDetail from './pages/Admin/Members/PartnerDetail';
import RealtorList from './pages/Admin/Members/RealtorList';
import RealtorDetail from './pages/Admin/Members/RealtorDetail';
import CustomerList from './pages/Admin/Members/CustomerList';
import ProspectStats from './pages/Admin/Members/ProspectStats';
// 관리자 — 서비스 요청
import RequestList from './pages/Admin/ServiceRequest/RequestList';
import ComplaintManage from './pages/Admin/ServiceRequest/ComplaintManage';
import AttentionItems from './pages/Admin/AttentionItems/AttentionItems';
// 관리자 — 정산
import PartnerReceivable from './pages/Admin/Settlement/PartnerReceivable';
import RealtorCommission from './pages/Admin/Settlement/RealtorCommission';
import RealtorReferral from './pages/Admin/Settlement/RealtorReferral';
import WithdrawalManage from './pages/Admin/Settlement/WithdrawalManage';
import AdminPaymentHistory from './pages/Admin/Settlement/PaymentHistory';
// 관리자 — 알림/공지
import AlimtalkLog from './pages/Admin/Notification/AlimtalkLog';
import NoticeList from './pages/Admin/Notification/NoticeList';
import FaqList from './pages/Admin/Notification/FaqList';
// 관리자 — 설정
import AdminAccount from './pages/Admin/Settings/AdminAccount';
import SystemLog from './pages/Admin/Settings/SystemLog';
import ServiceSettings from './pages/Admin/Settings/ServiceSettings';
import DbPricing from './pages/Admin/Settings/DbPricing';
import FormMail from './pages/Admin/Settings/FormMail';
import TermsSettings from './pages/Admin/Settings/TermsSettings';
import AdminConsents from './pages/Admin/Settings/AdminConsents';
import AppUpdate from './pages/Admin/Settings/AppUpdate';
import RealtorAutoAdminPage from './pages/Admin/RealtorAuto/RealtorAutoAdminPage';

// 제휴업체
import PartnerDashboard from './pages/Partner/Dashboard/PartnerDashboard';
import DbManagePage from './pages/Partner/DbManage/DbManagePage';
import PaymentManage from './pages/Partner/Payment/PaymentManage';
import PaymentProcess from './pages/Partner/Payment/PaymentProcess';
import PaymentRequest from './pages/Partner/Payment/PaymentRequest';
import PaymentHistory from './pages/Partner/Payment/PaymentHistory';
import SettlementManage from './pages/Partner/Settlement/SettlementManage';
import PartnerMyPage from './pages/Partner/MyPage/PartnerMyPage';
import CompanyInfo from './pages/Partner/MyPage/CompanyInfo';
import PartnerChangePassword from './pages/Partner/MyPage/ChangePassword';
import WithdrawAccount from './pages/Partner/MyPage/WithdrawAccount';
import ComplaintHandle from './pages/Partner/Complaints/ComplaintHandle';

// 부동산
import RealtorHome from './pages/Realtor/Home/RealtorHome';
import InviteCustomer from './pages/Realtor/Recommend/InviteCustomer';
import InviteRealtor from './pages/Realtor/Recommend/InviteRealtor';
import QrCode from './pages/Realtor/Recommend/QrCode';
import RecommendManage from './pages/Realtor/Recommend/RecommendManage';
import CustomerTrack from './pages/Realtor/Manage/CustomerTrack';
import LeadManage from './pages/Realtor/Manage/LeadManage';
import ProspectRegister from './pages/Realtor/Manage/ProspectRegister';
import ReferralStatus from './pages/Realtor/Manage/ReferralStatus';
import ReferralMembers from './pages/Realtor/Manage/ReferralMembers';
import SettlementStatus from './pages/Realtor/Manage/SettlementStatus';
import ComplaintList from './pages/Realtor/Manage/ComplaintList';
import SettlementCheck from './pages/Realtor/Manage/SettlementCheck';
import CustomerTrackHistory from './pages/Realtor/Manage/CustomerTrackHistory';
import RealtorProfile from './pages/Realtor/MyPage/Profile';
import ProfileEdit from './pages/Realtor/MyPage/ProfileEdit';
import AccountVerify from './pages/Realtor/MyPage/AccountVerify';
import NotifSettings from './pages/Realtor/MyPage/NotifSettings';
import RealtorWithdraw from './pages/Realtor/MyPage/Withdraw';
import WithdrawHistory from './pages/Realtor/MyPage/WithdrawHistory';
import CommissionDetail from './pages/Realtor/MyPage/CommissionDetail';
import RealtorStats from './pages/Realtor/MyPage/Stats';
import Performance from './pages/Realtor/MyPage/Performance';
import RewardStats from './pages/Realtor/MyPage/RewardStats';
import CustomerDbStats from './pages/Realtor/MyPage/CustomerDbStats';
import RealtorFaq from './pages/Realtor/MyPage/RealtorFaq';
import RealtorNotice from './pages/Realtor/MyPage/RealtorNotice';
import NotificationList from './pages/Realtor/Notification/NotificationList';
import InquiryList from './pages/Realtor/Inquiry/InquiryList';
import InquiryNew from './pages/Realtor/Inquiry/InquiryNew';

// 로그인 후 role에 따라 리다이렉트
const RoleRedirect = () => {
  const { role, loading, userData } = useAuth();

  if (loading) return <Spinner />;
  if (!role) return <Navigate to="/login" replace />;

  if (userData?.tempPassword) return <Navigate to="/change-password" replace />;

  switch (role) {
    case 'admin':
      return <Navigate to="/admin" replace />;
    case 'staff':
      return <Navigate to="/admin/mail/inbox" replace />;
    case 'partner':
      return <Navigate to="/partner" replace />;
    case 'realtor':
      return <Navigate to="/realtor" replace />;
    default:
      return <Navigate to="/login" replace />;
  }
};

// Next.js 정적 페이지로 풀 리로드
const ExternalRedirect = () => {
  window.location.reload();
  return null;
};

// 루트 엔트리: 비로그인 → 랜딩사이트로 이동 / 로그인 → role redirect
// 개발 모드에서는 랜딩 리다이렉트 대신 로그인 화면으로 보냄 (개발 편의)
const RootEntry = () => {
  const { userData, loading } = useAuth();
  if (loading) return <Spinner />;
  if (userData) return <RoleRedirect />;
  return <Navigate to="/login" replace />;
};

const VersionChecker = () => {
  const [toast, setToast] = React.useState(null);

  React.useEffect(() => {
    import('./services/appVersionService').then(({ checkAppUpdate }) => {
      checkAppUpdate({
        onUpdate: ({ version, content }) => {
          setToast(content || `새로운 버전 ${version}으로 업데이트 중...`);
        },
      });
    }).catch(() => {});
  }, []);

  if (!toast) return null;
  return (
    <div style={{
      position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)',
      background: '#fff', color: '#1F2937', padding: '12px 20px',
      borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      zIndex: 9999, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8,
      maxWidth: 360,
    }}>
      <span style={{ fontSize: 18 }}>&#x2139;&#xFE0F;</span>
      {toast}
    </div>
  );
};

const pendingToken = { current: null };
const navDepthRef = { current: 0 };

// SPA 라우트 변경을 RN에 알림 — 하드웨어 뒤로가기를 web 으로 위임받기 위함
const NavTracker = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const { userData, role } = useAuth();
  const auditUid = userData?.uid || '';

  React.useEffect(() => {
    void auditRouteView({
      path: location.pathname,
      search: location.search,
      navigationType,
      role,
      userData: auditUid ? { uid: auditUid, role } : null,
    });
  }, [location.pathname, location.search, navigationType, role, auditUid]);

  React.useEffect(() => {
    if (navigationType === 'PUSH') navDepthRef.current += 1;
    else if (navigationType === 'POP') navDepthRef.current = Math.max(0, navDepthRef.current - 1);
    // REPLACE 는 깊이 변화 없음

    sendToRN('NAV_STATE', {
      isRoot: navDepthRef.current === 0,
      path: location.pathname,
      canGoBackInWeb: navDepthRef.current > 0,
    });
  }, [location.pathname, location.search, navigationType]);

  return null;
};

const BridgeInit = () => {
  const { userData } = useAuth();
  const userDataRef = React.useRef(userData);
  const auditedReadyUidRef = React.useRef('');

  React.useEffect(() => {
    userDataRef.current = userData;
  }, [userData]);

  // 1회: bridge listener 초기화 + WEB_READY
  React.useEffect(() => {
    const cleanup = initBridgeListener();
    sendWebReady();

    // 토큰 수신 → 보류 or 즉시 저장
    const saveToken = (token, platform) => {
      pendingToken.current = { token, platform };
      const currentUser = userDataRef.current;
      if (currentUser?.uid) {
        saveFcmToken(currentUser.uid, token, platform || 'android');
        pendingToken.current = null;
      }
    };

    const unsubToken = onRNMessage('PUSH_TOKEN', (p) => {
      void auditWebViewMessage('PUSH_TOKEN', p, 'inbound', userDataRef.current);
      if (p?.token) saveToken(p.token, p.platform);
    });
    const unsubFcm = onRNMessage('FCM_TOKEN', (p) => {
      void auditWebViewMessage('FCM_TOKEN', p, 'inbound', userDataRef.current);
      if (p?.token) saveToken(p.token, 'android');
    });

    // 하드웨어 뒤로가기: 채널톡 팝업 열려있으면 닫기 우선, 아니면 SPA 히스토리 뒤로
    const unsubBack = onRNMessage('BACK_REQUEST', () => {
      void auditWebViewMessage('BACK_REQUEST', {}, 'inbound', userDataRef.current);
      if (isMessengerOpen()) {
        closeChannelTalk();
        return;
      }
      if (navDepthRef.current > 0) {
        window.history.back();
        return;
      }
      // 더 이상 갈 곳 없음 → 앱 종료 신호
      sendToRN('EXIT_APP', {});
      void auditWebViewMessage('EXIT_APP', {}, 'outbound', userDataRef.current);
    });

    // 루트 페이지에서 한 번 더 뒤로 누르면 종료하는 처리도 web 측에서 같이 함
    const unsubExit = onRNMessage('APP_EXIT_REQUEST', () => {
      void auditWebViewMessage('APP_EXIT_REQUEST', {}, 'inbound', userDataRef.current);
      sendToRN('EXIT_APP', {});
      void auditWebViewMessage('EXIT_APP', {}, 'outbound', userDataRef.current);
    });

    // 채널톡 팝업 열림 상태를 RN에 NAV_STATE 로 알려서 BACK_REQUEST 가 오게 함
    const unsubMessenger = subscribeMessengerState((open) => {
      sendToRN('NAV_STATE', { hasBlockingUI: open });
    });

    return () => { cleanup(); unsubToken(); unsubFcm(); unsubBack(); unsubExit(); unsubMessenger(); };
  }, []);

  React.useEffect(() => {
    if (!userData?.uid || auditedReadyUidRef.current === userData.uid) return;
    auditedReadyUidRef.current = userData.uid;
    void auditWebViewMessage('WEB_READY', {}, 'outbound', userData);
  }, [userData]);

  // userData 로드되면 보류 토큰 저장
  React.useEffect(() => {
    if (userData?.uid && pendingToken.current) {
      saveFcmToken(userData.uid, pendingToken.current.token, pendingToken.current.platform || 'android');
      pendingToken.current = null;
    }
  }, [userData?.uid]);

  return null;
};

const App = () => (
  <BrowserRouter>
    <VersionChecker />
    <BridgeInit />
    <NavTracker />
    <Routes>
      {/* 공통 */}
      <Route path="/realtorauto/connect" element={<RealtorAutoConnectPage />} />
      <Route path="/login" element={<PartnerLoginPage />} />
      <Route path="/partner/login" element={<PartnerLoginPage />} />
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/terms/:type" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/account-deletion" element={<AccountDeletionPage />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/fail" element={<PaymentFail />} />
      {/* 서비스별 랜딩 — Next.js 정적 페이지로 리로드 */}
      <Route path="/moving" element={<ExternalRedirect />} />
      <Route path="/internet" element={<ExternalRedirect />} />
      <Route path="/cleaning" element={<ExternalRedirect />} />
      <Route path="/al" element={<ExternalRedirect />} />
      <Route path="/co" element={<ExternalRedirect />} />
      {/* 알림톡 short-link 응답 — public (ri/rd/rc 입력형, rv 리뷰) */}
      <Route path="/ri/:code" element={<RespondInputPage />} />
      <Route path="/rd/:code" element={<RespondInputPage />} />
      <Route path="/rc/:code" element={<RespondInputPage />} />
      <Route path="/rv/:serviceRequestId" element={<ReviewPage />} />
      {/* 고객 신청 위저드 — public */}
      <Route path="/apply" element={<ApplyPage />} />
      <Route path="/apply/success" element={<ApplySuccessPage />} />
      <Route path="/apply/my" element={<CustomerMyPage />} />
      {/* 제휴 신청 — public */}
      <Route path="/realtor/signup" element={<RealtorSignupPage />} />
      <Route path="/partner/signup" element={<PartnerSignupPage />} />
      <Route path="/form" element={<FormPage />} />
      <Route path="/form/:realtorId" element={<FormPage />} />
      <Route path="/form/:realtorId/success" element={<FormSuccessPage />} />
      <Route path="/customer/mypage" element={<CustomerMyPage />} />
      <Route path="/change-password" element={<RequireAuth><ChangePasswordPage /></RequireAuth>} />
      <Route path="/consent" element={<RequireAuth><ConsentPage /></RequireAuth>} />

      {/* 소셜 로그인 플로우 */}
      <Route path="/auth/kakao/callback" element={<KakaoCallbackPage />} />
      <Route path="/social/role-select" element={<SocialRoleSelectPage />} />
      <Route path="/social/link-phone" element={<LinkPhonePage />} />
      <Route path="/social/merge-confirm" element={<MergeConfirmPage />} />
      <Route path="/social/no-account" element={<NoAccountPage />} />
      <Route path="/social/signup/complete" element={<SignupCompletePage />} />
      <Route path="/social/signup/:role" element={<SocialSignupPage />} />

      <Route path="/" element={<RootEntry />} />

      {/* 관리자 (admin + staff) */}
      <Route
        path="/admin"
        element={
          <RequireAuth>
            <RequireRole roles={['admin', 'staff']}>
              <AdminLayout />
            </RequireRole>
          </RequireAuth>
        }
      >
        <Route index element={(
          <RequireRole roles={['admin']} redirectTo="/admin/mail/inbox">
            <AdminDashboard />
          </RequireRole>
        )} />
        <Route path="mail" element={<Navigate to="/admin/mail/inbox" replace />} />
        <Route path="mail/:section" element={<AdminMailPage />} />

        {/* 관리자 전용 — staff는 메일 외 모든 경로에서 메일 받은편지함으로 이동 */}
        <Route element={(
          <RequireRole roles={['admin']} redirectTo="/admin/mail/inbox">
            <Outlet />
          </RequireRole>
        )}>
          {/* 회원관리 */}
          <Route path="members/partners" element={<PartnerList />} />
          <Route path="members/partners/:id" element={<PartnerDetail />} />
          <Route path="members/realtors" element={<RealtorList />} />
          <Route path="members/realtors/:id" element={<RealtorDetail />} />
          <Route path="members/customers" element={<CustomerList />} />
          <Route path="members/prospect-stats" element={<ProspectStats />} />
          {/* 서비스 요청 */}
          <Route path="service/requests" element={<RequestList />} />
          <Route path="service/complaints" element={<ComplaintManage />} />
          <Route path="attention-items" element={<AttentionItems />} />
          {/* 정산 관리 */}
          <Route path="settlement/partner" element={<PartnerReceivable />} />
          <Route path="settlement/commission" element={<RealtorCommission />} />
          <Route path="settlement/referral" element={<RealtorReferral />} />
          <Route path="settlement/withdrawal" element={<WithdrawalManage />} />
          <Route path="settlement/payments" element={<AdminPaymentHistory />} />
          {/* 알림/공지 */}
          <Route path="notification/alimtalk" element={<AlimtalkLog />} />
          <Route path="notification/notices" element={<NoticeList />} />
          <Route path="notification/faq" element={<FaqList />} />
          {/* 설정 */}
          <Route path="settings/accounts" element={<AdminAccount />} />
          <Route path="settings/logs" element={<SystemLog />} />
          <Route path="settings/service" element={<ServiceSettings />} />
          <Route path="settings/pricing" element={<DbPricing />} />
          <Route path="settings/formmail" element={<FormMail />} />
          <Route path="settings/terms" element={<TermsSettings />} />
          <Route path="settings/consents" element={<AdminConsents />} />
          <Route path="settings/app-update" element={<AppUpdate />} />
          <Route path="realtorauto/:section" element={<RealtorAutoAdminPage />} />
          {/* ADMIN */}
          <Route path="admin/permissions" element={<Navigate to="/admin/settings/accounts" replace />} />
          <Route path="admin/managers" element={<Navigate to="/admin/settings/accounts" replace />} />
        </Route>
      </Route>

      {/* 제휴업체 */}
      <Route
        path="/partner"
        element={
          <RequireAuth>
            <RequireRole roles={['partner']}>
              <RequireConsent>
                <PartnerLayout />
              </RequireConsent>
            </RequireRole>
          </RequireAuth>
        }
      >
        <Route index element={<PartnerDashboard />} />
        {/* DB 관리 */}
        <Route path="db" element={<DbManagePage />} />
        <Route path="db/my" element={<Navigate to="/partner/db?tab=mine" replace />} />
        <Route path="db/market" element={<Navigate to="/partner/db?tab=market" replace />} />
        {/* 결제 */}
        {/* 알림톡 8-3 "미수금 결제" 버튼이 /partner/payment 로 박혀있어 결제하기(settlement)로 흡수 */}
        <Route path="payment" element={<Navigate to="/partner/settlement" replace />} />
        <Route path="payment/manage" element={<PaymentManage />} />
        <Route path="payment/process/:id" element={<PaymentProcess />} />
        <Route path="payment/requests" element={<PaymentRequest />} />
        <Route path="payment/bills" element={<PaymentRequest />} />
        <Route path="payment/history" element={<PaymentHistory />} />
        {/* 정산 — 통합 페이지 */}
        <Route path="settlement" element={<SettlementManage />} />
        <Route path="settlement/expected" element={<SettlementManage />} />
        <Route path="settlement/history" element={<SettlementManage />} />
        <Route path="settlement/receivable" element={<SettlementManage />} />
        {/* 민원 처리 */}
        <Route path="complaints" element={<ComplaintHandle />} />
        {/* 내 정보 */}
        <Route path="mypage" element={<PartnerMyPage />} />
        <Route path="mypage/info" element={<CompanyInfo />} />
        <Route path="mypage/password" element={<PartnerChangePassword />} />
        <Route path="mypage/withdraw" element={<WithdrawAccount />} />
      </Route>

      {/* 부동산 */}
      <Route
        path="/realtor"
        element={
          <RequireAuth>
            <RequireRole roles={['realtor']}>
              <RequireConsent>
                <RealtorLayout />
              </RequireConsent>
            </RequireRole>
          </RequireAuth>
        }
      >
        <Route index element={<RealtorHome />} />
        {/* 추천하기 */}
        {/* InviteCustomer 컴포넌트 안에는 "파트너 초대 현황 + 파트너 단체 메시지"가, InviteRealtor 안에는 "고객 초대 현황"이 들어있어
            라우트 path 기준에 맞춰 element를 swap (컴포넌트 파일명과 path 의미가 반대) */}
        <Route path="recommend" element={<InviteRealtor />} />
        <Route path="recommend/customer" element={<InviteRealtor />} />
        <Route path="recommend/realtor" element={<InviteCustomer />} />
        <Route path="recommend/qr" element={<QrCode />} />
        <Route path="recommend/manage" element={<RecommendManage />} />
        {/* 관리·정산 */}
        <Route path="manage" element={<RecommendManage />} />
        <Route path="manage/customer" element={<CustomerTrack />} />
        <Route path="manage/lead" element={<LeadManage />} />
        <Route path="manage/prospect" element={<ProspectRegister />} />
        <Route path="manage/referral" element={<ReferralStatus />} />
        <Route path="manage/settlement" element={<SettlementStatus />} />
        <Route path="manage/complaint" element={<ComplaintList />} />
        <Route path="manage/referral-members" element={<ReferralMembers />} />
        <Route path="manage/settlement-check" element={<SettlementCheck />} />
        <Route path="manage/track-history" element={<CustomerTrackHistory />} />
        {/* 마이페이지 */}
        <Route path="mypage" element={<RealtorProfile />} />
        <Route path="mypage/profile" element={<RealtorProfile />} />
        <Route path="mypage/edit" element={<ProfileEdit />} />
        <Route path="mypage/account" element={<AccountVerify />} />
        <Route path="mypage/notification" element={<NotifSettings />} />
        <Route path="mypage/withdraw" element={<RealtorWithdraw />} />
        <Route path="mypage/withdraw-history" element={<WithdrawHistory />} />
        <Route path="mypage/commission" element={<CommissionDetail />} />
        <Route path="mypage/stats" element={<RealtorStats />} />
        <Route path="mypage/performance" element={<Performance />} />
        <Route path="mypage/reward-stats" element={<RewardStats />} />
        <Route path="mypage/customer-db-stats" element={<CustomerDbStats />} />
        <Route path="mypage/faq" element={<RealtorFaq />} />
        <Route path="mypage/notice" element={<RealtorNotice />} />
        {/* 알림 + 문의 */}
        <Route path="notifications" element={<NotificationList />} />
        <Route path="inquiry" element={<InquiryList />} />
        <Route path="inquiry/new" element={<InquiryNew />} />
      </Route>

      {/* 404 → 홈으로 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    <ChannelTalkModal />
  </BrowserRouter>
);

export default App;
